import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  SeverityBadge,
  Skeleton,
  cn,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarClock,
  ClipboardList,
  Hammer,
  MessageSquareWarning,
  Mic,
  Plus,
  Timer,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { fetchMe } from '../../api/auth.js';
import { listBuildings } from '../../api/buildings.js';
import { fetchComplianceDashboard } from '../../api/compliance.js';
import { fetchReminderSummary } from '../../api/reminders.js';
import { fetchViolationDashboard } from '../../api/violations.js';
import {
  fetchWorkOrderDashboard,
  type WorkOrderDashboardRow,
} from '../../api/work-orders.js';
import type { Violation } from '../../api/violations.js';

import { isOverdue, relativeTime, StatusPill } from './work-orders/lib.js';

export function DashboardPage() {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const woDashQuery = useQuery({
    queryKey: ['work-orders', 'dashboard'],
    queryFn: fetchWorkOrderDashboard,
  });
  const violationsQuery = useQuery({
    queryKey: ['violations', 'dashboard'],
    queryFn: fetchViolationDashboard,
  });
  const remindersQuery = useQuery({
    queryKey: ['reminders-summary'],
    queryFn: fetchReminderSummary,
  });
  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'today'],
    queryFn: () => listBuildings({ limit: 12, sort: 'name', order: 'asc' }),
  });
  const complianceQuery = useQuery({
    queryKey: ['compliance', 'dashboard'],
    queryFn: fetchComplianceDashboard,
  });

  const stats = woDashQuery.data?.stats;
  const firstName = meQuery.data?.user.name?.split(' ')[0];
  const hour = new Date().getHours();
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const buildings = buildingsQuery.data?.buildings ?? [];
  const unitTotal = buildings.reduce((sum, building) => sum + building.unitCount, 0);
  const buildingLabel = buildingsQuery.data
    ? `${buildings.length}${buildingsQuery.data.nextCursor ? '+' : ''} building${buildings.length === 1 ? '' : 's'}`
    : null;
  const criticalViolations =
    violationsQuery.data?.openBySeverity.find((row) => row.severity === 'critical')?.count ?? 0;
  const overdueCompliance = complianceQuery.data?.counts.overdue ?? 0;
  const needsNow = criticalViolations + overdueCompliance;
  const dueThisWeek =
    (stats?.upcoming7d ?? 0) +
    (complianceQuery.data?.counts.due_soon ?? 0) +
    (remindersQuery.data?.mineDueToday ?? 0);
  const activeRecords = complianceQuery.data?.totalActive ?? 0;
  const currentRecords = Math.max(activeRecords - overdueCompliance, 0);
  const compliancePct =
    activeRecords > 0 ? Math.round((currentRecords / activeRecords) * 100) : null;
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {hello}
            {firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="mt-1 text-[15px] text-fg-secondary">
            {todayLabel}
            {buildingLabel ? ` · ${buildingLabel}` : ''}
            {unitTotal > 0 ? ` · ${unitTotal} units` : ''}
            {needsNow > 0 ? ` · ${needsNow} thing${needsNow === 1 ? '' : 's'} need you` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" asChild>
            <Link to="/reminders">
              <Mic className="h-4 w-4" />
              Voice memo
            </Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/work-orders?new=1">
              <Plus className="h-4 w-4" />
              New work order
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          label="Needs action now"
          value={needsNow}
          loading={violationsQuery.isLoading || complianceQuery.isLoading}
          href="/violations"
          filled={needsNow > 0}
          detail={
            needsNow > 0
              ? `${criticalViolations} Class C · ${overdueCompliance} overdue compliance`
              : 'Nothing critical is waiting'
          }
          icon={AlertTriangle}
        />
        <KpiTile
          label="Overdue work orders"
          value={stats?.overdue ?? null}
          loading={woDashQuery.isLoading}
          href="/work-orders"
          detail={stats ? `${stats.open} open across the portfolio` : undefined}
          icon={Timer}
        />
        <KpiTile
          label="Due this week"
          value={dueThisWeek}
          loading={woDashQuery.isLoading || complianceQuery.isLoading}
          href="/work-orders"
          detail={`${stats?.upcoming7d ?? 0} work orders · ${complianceQuery.data?.counts.due_soon ?? 0} compliance · ${remindersQuery.data?.mineDueToday ?? 0} tasks`}
          icon={CalendarClock}
        />
        <KpiTile
          label="Compliance current"
          value={compliancePct != null ? `${compliancePct}%` : null}
          loading={complianceQuery.isLoading}
          href="/compliance"
          detail={
            activeRecords > 0 ? `${currentRecords} of ${activeRecords} records current` : 'No active records'
          }
          icon={ClipboardList}
        />
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="space-y-4">
        <ListPanel
          title="New violations"
          link="/violations"
          loading={violationsQuery.isLoading}
          emptyTitle="No new violations"
          emptyDescription="Nothing new in the last 7 days."
          emptyIcon={AlertTriangle}
          count={violationsQuery.data?.newLast7d.length ?? 0}
        >
          {(violationsQuery.data?.newLast7d ?? []).map((v) => (
            <ViolationRow key={v.id} violation={v} />
          ))}
        </ListPanel>

        <ListPanel
          title="New complaints"
          link="/violations/agency/_311"
          loading={violationsQuery.isLoading}
          emptyTitle="No new complaints"
          emptyDescription="No 311 / HPD complaints in the last 7 days."
          emptyIcon={MessageSquareWarning}
          count={violationsQuery.data?.newComplaintsLast7d.length ?? 0}
        >
          {(violationsQuery.data?.newComplaintsLast7d ?? []).map((v) => (
            <ViolationRow key={v.id} violation={v} />
          ))}
        </ListPanel>

        <ListPanel
          title="Most overdue"
          link="/work-orders"
          loading={woDashQuery.isLoading}
          emptyTitle="Nothing overdue"
          emptyDescription="No open work orders past their due date."
          emptyIcon={Timer}
          count={woDashQuery.data?.mostOverdue.length ?? 0}
        >
          {(woDashQuery.data?.mostOverdue ?? []).map((wo) => (
            <WorkOrderRow key={wo.id} wo={wo} />
          ))}
        </ListPanel>

        <ListPanel
          title="Upcoming seven days"
          link="/work-orders"
          loading={woDashQuery.isLoading}
          emptyTitle="Nothing upcoming"
          emptyDescription="No open work orders due in the next 7 days."
          emptyIcon={CalendarClock}
          count={woDashQuery.data?.upcoming7d.length ?? 0}
        >
          {(woDashQuery.data?.upcoming7d ?? []).map((wo) => (
            <WorkOrderRow key={wo.id} wo={wo} />
          ))}
        </ListPanel>

        <ListPanel
          title="Reminders"
          link="/reminders"
          loading={remindersQuery.isLoading}
          emptyTitle="Reminders clear"
          emptyDescription="No overdue reminders on your plate."
          emptyIcon={CalendarClock}
          className="lg:col-span-2"
          count={
            remindersQuery.data &&
            (remindersQuery.data.mineOverdue > 0 || remindersQuery.data.mineDueToday > 0)
              ? 1
              : 0
          }
        >
          {remindersQuery.data &&
          (remindersQuery.data.mineOverdue > 0 || remindersQuery.data.mineDueToday > 0) ? (
            <li className="px-2 py-2 text-sm">
              <Link to="/reminders" className="block rounded hover:bg-surface-muted">
                <div className="font-medium">
                  {remindersQuery.data.mineOverdue} overdue · {remindersQuery.data.mineDueToday} due
                  today
                </div>
                <div className="text-xs text-fg-muted">Open reminders inbox</div>
              </Link>
            </li>
          ) : null}
        </ListPanel>
        </div>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base">Buildings</CardTitle>
            <Link to="/buildings" className="text-xs text-fg-muted hover:underline">
              View all →
            </Link>
          </CardHeader>
          <CardContent>
            {buildingsQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : buildings.length === 0 ? (
              <EmptyState
                icon={Hammer}
                title="No buildings yet"
                description="Add a building to see the portfolio here."
              />
            ) : (
              <ul className="divide-y divide-border">
                {buildings.map((building) => (
                  <li key={building.id}>
                    <Link
                      to={`/buildings/${building.id}`}
                      className="flex items-center justify-between gap-3 py-3 text-sm hover:bg-surface-muted"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">{building.name}</div>
                        <div className="truncate text-xs text-fg-muted">
                          {building.unitCount} units
                          {building.borough ? ` · ${building.borough}` : ''}
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-xs text-fg-muted">
                        {building.openViolations > 0 ? (
                          <div className="font-semibold text-danger">
                            {building.openViolations} open
                          </div>
                        ) : (
                          <div>All current</div>
                        )}
                        <div>{building.openWorkOrders} open WOs</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function ListPanel({
  title,
  link,
  loading,
  emptyTitle,
  emptyDescription,
  emptyIcon: EmptyIcon,
  children,
  className,
  count,
}: {
  title: string;
  link: string;
  loading: boolean;
  emptyTitle: string;
  emptyDescription: string;
  emptyIcon: typeof Hammer;
  children: React.ReactNode;
  className?: string;
  count: number;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <Link to={link} className="text-xs text-fg-muted hover:underline">
          View all →
        </Link>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : count === 0 ? (
          <EmptyState icon={EmptyIcon} title={emptyTitle} description={emptyDescription} />
        ) : (
          <ul className="space-y-1">{children}</ul>
        )}
      </CardContent>
    </Card>
  );
}

function ViolationRow({ violation: v }: { violation: Violation }) {
  return (
    <li>
      <Link
        to={`/violations/${v.id}`}
        className="flex items-center justify-between gap-3 rounded px-2 py-2 text-sm hover:bg-surface-muted"
      >
        <div className="min-w-0">
          <div className="truncate font-medium">{v.summary}</div>
          <div className="truncate text-xs text-fg-muted">
            {v.buildingName ?? 'Building'} · {v.agency}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SeverityBadge level={v.severity} dotOnly />
          <span className="text-xs text-fg-muted">{relativeTime(v.firstSeenAt)}</span>
        </div>
      </Link>
    </li>
  );
}

function WorkOrderRow({ wo }: { wo: WorkOrderDashboardRow }) {
  return (
    <li>
      <Link
        to={`/work-orders/${wo.id}`}
        className="flex items-center justify-between gap-3 rounded px-2 py-2 text-sm hover:bg-surface-muted"
      >
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <span className="font-mono text-xs text-fg-muted">{wo.displayNumber}</span>
            <span className="truncate font-medium">{wo.title}</span>
          </div>
          <div className="truncate text-xs text-fg-muted">{wo.buildingName}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SeverityBadge level={wo.severity} dotOnly />
          <StatusPill status={wo.status} />
          {wo.dueAt ? (
            <span
              className={cn(
                'text-xs',
                isOverdue(wo.dueAt, wo.status) ? 'font-medium text-danger' : 'text-fg-muted',
              )}
            >
              {new Date(wo.dueAt).toLocaleDateString()}
            </span>
          ) : null}
        </div>
      </Link>
    </li>
  );
}

function KpiTile({
  label,
  value,
  detail,
  loading,
  href,
  filled,
  icon: Icon,
}: {
  label: string;
  value: number | string | null;
  detail?: string;
  loading: boolean;
  href: string;
  filled?: boolean;
  icon: typeof Hammer;
}) {
  return (
    <Link
      to={href}
      className={cn(
        'block rounded-xl border p-4 transition-colors',
        filled
          ? 'border-danger bg-danger text-white hover:bg-danger/90'
          : 'border-border bg-surface hover:border-border-strong',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            'text-[11px] font-semibold uppercase tracking-wide',
            filled ? 'text-white/80' : 'text-fg-muted',
          )}
        >
          {label}
        </span>
        <Icon className={cn('h-4 w-4', filled ? 'text-white/80' : 'text-fg-muted')} />
      </div>
      {loading ? (
        <Skeleton className={cn('mt-3 h-8 w-14', filled && 'bg-white/30')} />
      ) : (
        <p className="mt-2 text-[32px] font-bold leading-none tabular-nums">{value ?? '—'}</p>
      )}
      {detail ? (
        <p className={cn('mt-2 text-xs leading-snug', filled ? 'text-white/85' : 'text-fg-secondary')}>
          {detail}
        </p>
      ) : null}
    </Link>
  );
}
